import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/* ---------------- helpers (tiny + safe) ---------------- */
const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const getId = (u) => (u && typeof u === "object" ? u._id : u) || "";
const getName = (u) =>
  (u && typeof u === "object" && (u.name || u.userId)) || "";
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? "—" : dt.toLocaleDateString();
};
const normalizeTopics = (topics) =>
  asArray(topics)
    .map((t) =>
      typeof t === "object" ? t?.topicTypes || t?.name || t?.title || t?._id : t
    )
    .filter(Boolean);

async function safeFetchJSON(url, { signal } = {}) {
  const res = await fetch(url, { signal });
  let data = null;
  try {
    data = await res.json();
  } catch {}
  if (!res.ok)
    throw new Error(
      data?.message || data?.error || `Request failed (${res.status})`
    );
  return data;
}

/* ---------------- component ---------------- */
export default function SpecificMeetingAllPanel({
  apiBase,
  projectId,
  onBack,
  title = "All Specific Meetings",
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [q, setQ] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [advOpen, setAdvOpen] = useState(false);
  const [adv, setAdv] = useState({
    from: "",
    to: "",
    minHours: "",
    maxHours: "",
    hasGeo: "",
    topic: "",
    attendee: "",
  });

  const abortRef = useRef(null);

  /* ---------------- fetch ---------------- */
  useEffect(() => {
    const ac = new AbortController();
    abortRef.current = ac;

    (async () => {
      try {
        setLoading(true);
        setErr("");
        const url = new URL(apiBase + "/specific");
        url.searchParams.set("project", projectId);
        const res = await safeFetchJSON(url.toString(), { signal: ac.signal });
        let list = Array.isArray(res) ? res : res?.data || [];

        list = list.filter((it) => {
          const p = it?.project;
          const id = typeof p === "object" ? p?._id : p;
          return String(id || "") === String(projectId || "");
        });

        setRows(list);
      } catch (e) {
        if (e.name !== "AbortError")
          setErr(e.message || "Failed to load meetings.");
      } finally {
        setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [apiBase, projectId]);

  /* ---------------- options ---------------- */
  const ownerOptions = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      for (const u of [r?.instructionBy, r?.createdby]) {
        const id = getId(u);
        if (id && !map.has(id)) map.set(id, getName(u) || "Unnamed");
      }
    }
    return [...map].map(([id, name]) => ({ id, name }));
  }, [rows]);

  /* ---------------- filtering + summary ---------------- */
  const parseBoundary = useCallback((s, end = false) => {
    if (!s) return null;
    const hasTime = /T|\d{1,2}:\d{2}/.test(s);
    const d = new Date(hasTime ? s : `${s}T${end ? "23:59:59" : "00:00:00"}`);
    return Number.isNaN(d.getTime()) ? null : d;
  }, []);

  const { filtered, instructorSummary, quickStats } = useMemo(() => {
    const qi = q.trim().toLowerCase();
    const topicNeedle = adv.topic.trim().toLowerCase();
    const attendeeNeedle = adv.attendee.trim().toLowerCase();
    const fromT = parseBoundary(adv.from)?.getTime() ?? null;
    const toT = parseBoundary(adv.to, true)?.getTime() ?? null;
    const minH = adv.minHours ? Number(adv.minHours) : null;
    const maxH = adv.maxHours ? Number(adv.maxHours) : null;
    const wantGeo = adv.hasGeo;

    const out = [];
    const byOwner = new Map();

    let totalMeetings = 0;
    let totalAttendance = 0;
    const allTopics = new Set();

    for (const it of rows) {
      const topics = normalizeTopics(it?.typeOfTopic);
      const attendees = asArray(it?.attendees);
      const hay = [
        it?.reportId,
        it?.commentsBox,
        it?.geotagging,
        ...topics,
        ...attendees,
        getName(it?.instructionBy),
        getName(it?.createdby),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (qi && !hay.includes(qi)) continue;

      if (ownerId) {
        const ids = [getId(it?.instructionBy), getId(it?.createdby)].filter(
          Boolean
        );
        if (!ids.map(String).includes(String(ownerId))) continue;
      }

      const base = it?.date || it?.createdAt;
      const t = base ? new Date(base).getTime() : 0;
      if (fromT && (!t || t < fromT)) continue;
      if (toT && (!t || t > toT)) continue;

      if (topicNeedle && !topics.join(" ").toLowerCase().includes(topicNeedle))
        continue;
      if (
        attendeeNeedle &&
        !attendees.join(" ").toLowerCase().includes(attendeeNeedle)
      )
        continue;

      const hrs = Number(it?.attendeesHours ?? NaN);
      if (minH !== null && !Number.isNaN(hrs) && hrs < minH) continue;
      if (maxH !== null && !Number.isNaN(hrs) && hrs > maxH) continue;

      if (wantGeo === "yes" && !it?.geotagging) continue;
      if (wantGeo === "no" && it?.geotagging) continue;

      out.push(it);

      totalMeetings += 1;
      const attendance = Number(it?.attendance) || 0;
      totalAttendance += attendance;
      topics.forEach((tpc) => allTopics.add(tpc));

      const ownerLike = it?.instructionBy || it?.createdby || null;
      const oid = getId(ownerLike) || "__unassigned__";
      const oname =
        oid === "__unassigned__"
          ? "Unassigned"
          : getName(ownerLike) || "Unnamed";
      let ent = byOwner.get(oid);
      if (!ent) {
        ent = {
          id: oid,
          name: oname,
          totalMeetings: 0,
          totalAttendance: 0,
          topics: new Set(),
          dates: new Set(),
          firstDate: null,
          lastDate: null,
        };
        byOwner.set(oid, ent);
      }
      ent.totalMeetings += 1;
      ent.totalAttendance += attendance;
      topics.forEach((t) => t && ent.topics.add(t));
      if (it?.date) {
        const ds = fmtDate(it.date);
        ent.dates.add(ds);
        const dt = new Date(it.date);
        if (!ent.firstDate || dt < ent.firstDate) ent.firstDate = dt;
        if (!ent.lastDate || dt > ent.lastDate) ent.lastDate = dt;
      }
    }

    const instructorSummary = [...byOwner.values()].sort((a, b) => {
      if (a.id === "__unassigned__") return 1;
      if (b.id === "__unassigned__") return -1;
      return a.name.localeCompare(b.name);
    });

    const quickStats = {
      totalMeetings,
      totalInstructors: instructorSummary.length,
      uniqueTopics: allTopics.size,
      totalAttendance,
    };

    return { filtered: out, instructorSummary, quickStats };
  }, [rows, q, ownerId, adv, parseBoundary]);

  return (
    <div>
      {/* top bar */}
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h4 className="text-base font-semibold">
          {title}
          <span className="ml-2 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-blue-200">
            {filtered.length}
          </span>
        </h4>

        {/* quick analytics */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div className="rounded-xl bg-blue-50 p-3">
            <div className="text-gray-500 text-xs">Total Meetings</div>
            <div className="font-bold text-xl">{quickStats.totalMeetings}</div>
          </div>
          <div className="rounded-xl bg-green-50 p-3">
            <div className="text-gray-500 text-xs">Total Instructors</div>
            <div className="font-bold text-xl">
              {quickStats.totalInstructors}
            </div>
          </div>
          <div className="rounded-xl bg-orange-50 p-3">
            <div className="text-gray-500 text-xs">Total Attendance</div>
            <div className="font-bold text-xl">
              {quickStats.totalAttendance}
            </div>
          </div>
        </div>

        {/* filters */}
        <div className="flex flex-wrap gap-2">
          <input
            className="w-56 rounded-xl border px-3 py-2 text-sm"
            placeholder="Search reportId, topic, comments..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <select
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
            className="rounded-xl border px-2 py-2 text-sm"
            title="Filter by instructor"
          >
            <option value="">All Instructors</option>
            {ownerOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => setAdvOpen((v) => !v)}
            className="rounded-xl border px-3 py-2 text-sm"
          >
            {advOpen ? "Close Filters" : "Advanced Filters"}
          </button>
          {onBack && (
            <button
              onClick={onBack}
              className="rounded-xl border px-3 py-2 text-sm"
            >
              ← Back
            </button>
          )}
        </div>
      </div>

      {/* summary table */}
      {loading ? (
        <div>Loading…</div>
      ) : err ? (
        <div className="text-red-600">{err}</div>
      ) : (
        <div className="overflow-hidden rounded-2xl border shadow-sm bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-100">
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                <th className="px-4 py-3">Instructor</th>
                <th className="px-4 py-3">Total Meetings</th>
                <th className="px-4 py-3">Total Attendance</th>
                <th className="px-4 py-3">Dates Covered</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {instructorSummary.map((row) => (
                <tr key={row.id} className="hover:bg-indigo-50">
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {row.name}
                  </td>
                  <td className="px-4 py-3">{row.totalMeetings}</td>
                  <td className="px-4 py-3">{row.totalAttendance}</td>
                  <td className="px-4 py-3">
                    {[...row.dates].join(", ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
